import { Button, Modal } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import LeadEventForm from './LeadEventForm';
import { useEffect, useState } from 'react';
import { DateTime as DT } from 'luxon';
import spinnerTitle from 'components/others/spinnerTitle';
import SvgSave from 'components/svg/SvgSave';
import { useToast } from 'context/ToastContext';
import * as apiLead from 'api/apiLeads';
import type { LeadCloudFileType, LeadEventAddDataType } from '@econtractor/types';
import { uploadFilesWithOverallProgress } from 'utils/helpers/featchFiles/uploadFileWithProgress';
import { useNeedPay } from 'modules/admin';
import vconst from 'utils/helpers/structs/vconst';

interface LeadEventModalProps {
    newEvent: any;
    setNewEvent: React.Dispatch<React.SetStateAction<any>>;
    createEvent: (eventPatch?: Partial<LeadEventAddDataType>) => Promise<boolean>;
}

const appendPostponedNote = (eventInfo: string | undefined, pendingDt: number, label: string): string => {
    const dateStr = DT.fromMillis(pendingDt, { zone: 'local' }).toFormat('dd.MM.yyyy');
    const text = `${label} на ${dateStr}`;
    const note = `<p>${text}</p>`;
    const current = String(eventInfo || '');
    if (current.includes(text)) return current;
    return `${current}${note}`;
};

const LeadEventModal = ({ newEvent, setNewEvent, createEvent }: LeadEventModalProps) => {
    const { t } = useTranslation(['Leads', 'common']);
    const { showToast } = useToast();
    const { needPay } = useNeedPay();
    const [progress, setProgress] = useState<number | boolean>(false);
    const [cloudProgress, setCloudProgress] = useState<number | boolean>(false);
    const [saving, setSaving] = useState(false);
    const [pendingCloudFiles, setPendingCloudFiles] = useState<File[]>([]);

    useEffect(() => {
        if (!newEvent) {
            setPendingCloudFiles([]);
            setCloudProgress(false);
        }
    }, [newEvent]);

    const uploadCloudFiles = async (leadId: number): Promise<LeadCloudFileType[]> => {
        if (!pendingCloudFiles.length) return [];

        const filesMeta = pendingCloudFiles.map((file) => ({
            fileName: file.name,
            contentType: file.type || 'application/octet-stream',
            fileSize: file.size,
        }));

        const uploadConfigs = await apiLead.getCloudflareUploadUrls(leadId, filesMeta);  

        if (!uploadConfigs || uploadConfigs.length !== pendingCloudFiles.length) {
            throw new Error('Upload URLs are not available');
        }

        const uploaded = await uploadFilesWithOverallProgress(pendingCloudFiles, uploadConfigs, setCloudProgress);

        setCloudProgress(100);
        return uploaded.filter((item) => item.success).map((item) => item.config);
    };

    const handleClickSave = async () => {
        if (needPay()) return;
        if (
            Number(newEvent?.statusID) === vconst.leadPostponed &&
            !(Number(newEvent?.leadPendingDt) > 0)
        ) {
            showToast(t('leadStatus.messPendingDtRequired'), 'warning');
            return;
        }
        setSaving(true);
        let cloudFiles: LeadCloudFileType[] = [];

        if (pendingCloudFiles.length && newEvent?.leadId) {
            try {
                setCloudProgress(1);
                cloudFiles = await uploadCloudFiles(Number(newEvent.leadId));  
                if (cloudFiles.length !== pendingCloudFiles.length) {
                    showToast(t('common:messError'), 'warning');
                }
            } catch (e) {
                console.error(e);
                showToast(t('common:messError'), 'warning');
            } finally {
                setCloudProgress(false);
            }
        }

        const eventPatch: Partial<LeadEventAddDataType> = {
            eventAttachCloud: cloudFiles,
        };
        if (
            Number(newEvent?.statusID) === vconst.leadPostponed &&
            Number(newEvent?.leadPendingDt) > 0
        ) {
            eventPatch.eventInfo = appendPostponedNote(
                newEvent?.eventInfo,
                Number(newEvent.leadPendingDt),
                t('status.350'),
            );
        }

        const resCreate = await createEvent(eventPatch);
        setSaving(false);
        if (resCreate) {
            setPendingCloudFiles([]);
            setNewEvent(null);
        }
    };

    const formDisabled = saving || progress !== false || cloudProgress !== false;

    return (
        <Modal
            show={newEvent !== null}
            onHide={() => !formDisabled && setNewEvent(null)}
            backdrop="static"
            size="xl"
            keyboard={false}
        >
            <Modal.Header closeButton={!formDisabled}>
                <Modal.Title>{t('leadStatus.titleNewEvent')}</Modal.Title>
            </Modal.Header>
            <Modal.Body className="pt-0">
                <LeadEventForm
                    disabled={formDisabled}
                    progress={progress}
                    setProgress={setProgress}
                    cloudProgress={cloudProgress}
                    pendingCloudFiles={pendingCloudFiles}
                    setPendingCloudFiles={setPendingCloudFiles}
                    newEvent={newEvent}
                    setNewEvent={setNewEvent}
                />
            </Modal.Body>
            <Modal.Footer>
                <Button
                    className="me-1"
                    variant="secondary"
                    disabled={formDisabled}
                    onClick={() => setNewEvent(null)}
                >
                    {t('common:buttons.close')}
                </Button>
                <Button variant="info" disabled={formDisabled} onClick={handleClickSave}>
                    {spinnerTitle(
                        <>
                            <SvgSave className="me-2 mtop-2" />
                            {t('common:buttons.save')}
                        </>,
                        saving,
                        t('common:buttons.saving'),
                    )}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default LeadEventModal;
